import React, { useState, useMemo, useEffect, useRef } from "react";
import { useReactTable, getCoreRowModel, getPaginationRowModel, type SortingState, getSortedRowModel } from "@tanstack/react-table";
import dayjs from "dayjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import TablaGestionArchivosVista from "../vista/tablaGestionArchivosVista";
import { obtenerColumnas } from "../vista/constantesTablaGestionArchivos";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import type { IVisitaCliente } from "../../../modelos/GestionArchivos/entidades/IVisitaCliente";
import { GestionArchivosModelo } from "../../../modelos/GestionArchivos";
import type { IFiltroVisitaCliente } from "../../../modelos/GestionArchivos/entidades/IFiltroVisitaCliente";
import { useAutenticacion } from "../../../globales/contextos/authContext";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion, type ICatalogo } from "../../../globales/interfaces";
import { EnumTipoTarea } from "../../Tareas/enums/enumTipoTarea";
import { esUsuarioInterno, puedeVerFormatosNbs, soloVisitaInstalacion } from "../utils/permisosGestionArchivos";
// import type { ICliente } from "../../../modelos/Clientes/entidades/ICliente";
// import { ClienteModelo } from "../../../modelos/Clientes";
// import { estadoInicialFiltroCliente } from "../../clientes/interfaces";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";
import { estadoInicialFiltroProyecto } from "../../proyectos/interfaces";
import ModalPrevisualizarPDF from "../vista/modalPrevisualizarPDF";
import { TipoTareaModelo } from "../../../modelos/TipoTarea";
import { TareaModelo } from "../../../modelos/Tarea";
import { estadoInicialFiltroTarea } from "../../Tareas/interfaces";
import { ErrorReporteNbs, NbsReportesModelo } from "../../../modelos/NbsReportes";
import type { IArchivoNbs } from "../../../modelos/NbsReportes/entidades/IFiltroNbsReporte";
import type { INbsReporteDisponible } from "../../../modelos/NbsReportes/entidades/INbsReporteDisponible";

// Errores del reporte NBS: traen el mensaje del backend; los demás, el mensaje genérico de cada acción.
const mensajeDeError = (error: unknown, porDefecto: string) =>
    error instanceof ErrorReporteNbs ? error.message : porDefecto;

const GestionArchivosCV: React.FC = () => {
    // --- ESTADOS ---
    const { usuario } = useAutenticacion();
    const { mostrarNotificacion, removerNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();
    const [listaVisitas, setListaVisitas] = useState<IVisitaCliente[]>([]);
    const [idsSeleccionados, setIdsSeleccionados] = useState<Record<number, boolean>>({});
    const [filtrosHabilitados, setFiltrosHabilitados] = useState<boolean>(true);
    const [ordenamiento, setOrdenamiento] = useState<SortingState>([]);
    const [indicePagina, setIndicePagina] = useState(0);
    const esInterno = esUsuarioInterno(usuario);
    const verFormatosNbs = puedeVerFormatosNbs(usuario);
    const tipoTareaFijo = soloVisitaInstalacion(usuario) ? EnumTipoTarea.VisitaInstalacion : null;
    const [filtroVisitaCliente, setFiltroVisitaCliente] = useState<IFiltroVisitaCliente>({
        Identificacion: esInterno ? '' : usuario?.Identificacion,
        FechaInicio: dayjs().startOf('day').toISOString(),
        FechaFin: dayjs().add(1, 'day').endOf('day').toISOString(),
        TipoServicioId: tipoTareaFijo
    });
    // const [clientes, setClientes] = useState<ICliente[]>([]);
    const [proyectos, setProyectos] = useState<IProyecto[]>([]);
    const [tiposTarea, setTiposTarea] = useState<ICatalogo[]>([]);
    const [modalPDFAbierto, setModalPDFAbierto] = useState(false);
    const [urlPDFActual, setUrlPDFActual] = useState("");
    const [nombreArchivoActual, setNombreArchivoActual] = useState("");
    const [visitaActual, setVisitaActual] = useState<IVisitaCliente | null>(null);
    const [archivoNbsActual, setArchivoNbsActual] = useState<IArchivoNbs | null>(null);
    // Visitas de Visita Instalación (NBS): su archivo es el formato NBS y no el reporte de visita.
    const [formatosNbsPorVisita, setFormatosNbsPorVisita] = useState<Record<number, INbsReporteDisponible>>({});
    // Tareas ya validadas: mientras no lo estén, "Archivo" no muestra nombre para no cambiarlo después.
    const [tareasNbsValidadas, setTareasNbsValidadas] = useState<Record<number, boolean>>({});
    // Una consulta de formatos NBS por tarea, compartida entre la carga de la página y los clics.
    const consultasNbsPorTarea = useRef(new Map<number, Promise<INbsReporteDisponible[]>>());

    const modeloGestionArchivos = new GestionArchivosModelo();
    const modeloNbsReportes = new NbsReportesModelo();
    const modeloTipoTarea = new TipoTareaModelo();
    const modeloTarea = new TareaModelo();
    // const modeloCliente = new ClienteModelo();
    const modeloProyecto = new ProyectoModelo();
    const TAMANO_PAGINA = 15;

    useEffect(() => {
        if (esInterno) {
            // consultarClientes();
            consultarProyectos();
            consultarTiposTarea();
        } else {
            consultarProyectos();
            consultarTiposTarea();
            consultarVisitasCliente(filtroVisitaCliente);
        }
    }, [usuario]);

    // const consultarClientes = async () => {
    //     try {
    //         manejarCargando(true);
    //         const respuesta = await modeloCliente.obtenerListaClientes(estadoInicialFiltroCliente);
    //         if (respuesta && respuesta.Data) {
    //             setClientes(respuesta.Data);
    //         } else {
    //             setClientes([]);
    //         }
    //     }
    //     catch {
    //         mostrarNotificacion(EnumTipoNotificacion.Error, 'Error de consulta', 'No fue posible obtener la lista de clientes. Intente de nuevo más tarde.', 5000);
    //     }
    //     finally {
    //         manejarCargando(false);
    //     }
    // }

    const consultarProyectos = async () => {
        try {
            manejarCargando(true);
            const respuesta = await modeloProyecto.obtenerListaProyectos(estadoInicialFiltroProyecto.NitProyecto);
            if (respuesta && respuesta.Data) {
                setProyectos(respuesta.Data);

                // Si el usuario es un cliente y tiene un único proyecto relacionado, lo autoseleccionamos
                if (!esInterno && usuario?.Identificacion) {
                    const proyectosDelCliente = respuesta.Data.filter(
                        p => p.Cliente?.Identificacion?.trim() === usuario.Identificacion?.trim()
                    );
                    if (proyectosDelCliente.length === 1) {
                        setFiltroVisitaCliente(prev => ({
                            ...prev,
                            ProyectoId: proyectosDelCliente[0].Id
                        }));
                    }
                }
            } else {
                setProyectos([]);
            }
        }
        catch (error) {
            mostrarNotificacion(EnumTipoNotificacion.Error, 'Error de consulta', 'No fue posible obtener la lista de proyectos. Intente de nuevo más tarde.', 5000);
        }
        finally {
            manejarCargando(false);
        }
    }

    const consultarTiposTarea = async () => {
        try {
            manejarCargando(true);
            const respuesta = await modeloTipoTarea.obtenerListaTiposTarea();
            if (respuesta && respuesta.Data) {
                setTiposTarea(respuesta.Data);
            } else {
                setTiposTarea([]);
            }
        }
        catch (error) {
            mostrarNotificacion(EnumTipoNotificacion.Error, 'Error de consulta', 'No fue posible obtener la lista de tipos de tarea. Intente de nuevo más tarde.', 5000);
        }
        finally {
            manejarCargando(false);
        }
    }

    const consultarTareas = async () => {
        try {
            manejarCargando(true, undefined, true);
            const tareas = await modeloTarea.obtenerListaTareas({
                ...estadoInicialFiltroTarea,
                TipoServicioId: filtroVisitaCliente.TipoServicioId || null,
                ProyectoId: filtroVisitaCliente.ProyectoId || null,
                FechaFin: filtroVisitaCliente.FechaFin || null,
                FechaInicio: filtroVisitaCliente.FechaInicio || null
            });
            return tareas.Data || [];
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al consultar tareas',
                'No fue posible consultar las tareas. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        }
        finally {
            manejarCargando(false);
        }
    }

    const manejarCambioFiltroVisita = async (campo: keyof IFiltroVisitaCliente, valor: IFiltroVisitaCliente[keyof IFiltroVisitaCliente]) => {
        setFiltroVisitaCliente((prevFiltroVisita) => ({
            ...prevFiltroVisita,
            [campo]: valor
        }));
    };

    const validarCamposConsulta = () => {
        let fechasValidas = true;
        let esValido = true;

        if (!filtroVisitaCliente.FechaInicio.trim()) {
            fechasValidas = false;
        }

        if (!filtroVisitaCliente.FechaFin.trim()) {
            fechasValidas = false;
        }

        if (filtroVisitaCliente.Identificacion === '' && esInterno) {
            esValido = false;
        }

        if (!fechasValidas && esValido) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Debes seleccionar un rango de fechas para realizar la consulta.",
                5000
            );
        }

        if (!esValido && fechasValidas) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Debes seleccionar un proyecto para realizar la consulta.",
                5000
            );
        }

        if (!fechasValidas && !esValido) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Debes seleccionar un cliente y un rango de fechas para realizar la consulta.",
                5000
            );
        }

        return fechasValidas && esValido;
    };

    const consultarVisitasCliente = async (filtro: IFiltroVisitaCliente) => {
        if (!validarCamposConsulta()) return
        try {
            manejarCargando(true, "Consultando registros...");
            const respuesta = await modeloGestionArchivos.obtenerVisitasCliente(filtro);

            if (respuesta && respuesta.Data && respuesta.Data.length > 0) {
                let visitasFiltradas = respuesta.Data;

                if (filtro.ProyectoId) {
                    const proyectoSeleccionado = proyectos.find(
                        p => p.Id === Number(filtro.ProyectoId)
                    );
                    visitasFiltradas = visitasFiltradas.filter(
                        v => v.NombreProyecto.trim() === proyectoSeleccionado?.Nombre.trim()
                    );
                }

                if (filtro.TareaId) {
                    visitasFiltradas = visitasFiltradas.filter(v => v.IdTarea === filtro.TareaId);
                }

                if (filtro.TipoServicioId) {
                    const tareas = await consultarTareas();
                    if (tareas && tareas.length > 0) {
                        const tareasDelTipo = tareas
                            .filter(t => t.TipoServicioId === filtro.TipoServicioId)
                            .map(t => t.Id);

                        visitasFiltradas = visitasFiltradas.filter(
                            v => tareasDelTipo.includes(v.IdTarea)
                        );
                    } else {
                        visitasFiltradas = [];
                    }
                }

                // Sin permiso NBS no se listan las visitas de Visita Instalación (sus formatos solo los ven
                // Gerente y Planeador). Si no se pueden identificar, la consulta falla en lugar de mostrarlas.
                if (!verFormatosNbs && visitasFiltradas.length > 0) {
                    const tareasNbs = await modeloTarea.obtenerListaTareas({
                        ...estadoInicialFiltroTarea,
                        TipoServicioId: EnumTipoTarea.VisitaInstalacion,
                        ProyectoId: filtro.ProyectoId ? Number(filtro.ProyectoId) : null
                    });
                    const idsTareasNbs = new Set((tareasNbs?.Data ?? []).map(t => t.Id));
                    visitasFiltradas = visitasFiltradas.filter(v => !idsTareasNbs.has(v.IdTarea));
                }

                if (visitasFiltradas.length === 0) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        "Sin resultados",
                        "No se encontraron visitas que coincidan con los filtros seleccionados.",
                        5000
                    );
                } else {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Success,
                        "Consulta exitosa",
                        `Se encontraron ${visitasFiltradas.length} visitas en el rango de fechas especificado`,
                        5000
                    );
                }
                setListaVisitas(visitasFiltradas);
            } else {
                setListaVisitas([]);
            }

            setIdsSeleccionados({});
            setIndicePagina(0);
            consultasNbsPorTarea.current.clear();
            setFormatosNbsPorVisita({});
            setTareasNbsValidadas({});

        } catch (error) {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error de consulta',
                'No fue posible obtener los archivos. Intente de nuevo más tarde.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };

    // --- REPORTES NBS ---
    // Cada visita NBS tiene un único formato; se identifica entre los formatos de su tarea por el IdVisita.
    const consultarFormatosNbsTarea = (tareaId: number): Promise<INbsReporteDisponible[]> => {
        const enCurso = consultasNbsPorTarea.current.get(tareaId);
        if (enCurso) return enCurso;

        const consulta = modeloNbsReportes.obtenerDisponibles({ tareaId }, true)
            .then(respuesta => {
                const formatos = respuesta?.Data ?? [];
                if (formatos.length > 0) {
                    setFormatosNbsPorVisita(prev => ({ ...prev, ...Object.fromEntries(formatos.map(f => [f.VisitaId, f])) }));
                }
                return formatos;
            })
            .catch(error => {
                // Se descarta para reintentar en el próximo clic.
                consultasNbsPorTarea.current.delete(tareaId);
                throw error;
            })
            .finally(() => setTareasNbsValidadas(prev => ({ ...prev, [tareaId]: true })));

        consultasNbsPorTarea.current.set(tareaId, consulta);
        return consulta;
    };

    // Sin permiso NBS no se consulta: esas visitas ya se excluyeron del listado.
    const obtenerFormatoNbs = async (visita: IVisitaCliente): Promise<INbsReporteDisponible | undefined> =>
        verFormatosNbs
            ? (await consultarFormatosNbsTarea(visita.IdTarea)).find(f => f.VisitaId === visita.IdVisita)
            : undefined;

    // ZIP con los reportes seleccionados cuando hay visitas NBS: los demás salen del ZIP de siempre y los NBS
    // se agregan uno a uno. Si alguno NBS falla se omite y se lista en _no_generados.txt.
    const descargarZipConNbs = async (idsNbs: number[], idsOtros: number[]) => {
        const zip = new JSZip();

        if (idsOtros.length > 1) {
            await zip.loadAsync(await modeloGestionArchivos.obtenerZipVisitas(idsOtros));
        } else if (idsOtros.length === 1) {
            const respuesta = await modeloGestionArchivos.descargarArchivoVisita(idsOtros[0]);
            if (respuesta?.Data?.ArchivoPdfBase64) {
                zip.file(respuesta.Data.NombreArchivo || `Reporte_${idsOtros[0]}.pdf`, respuesta.Data.ArchivoPdfBase64.replace(/\s/g, ''), { base64: true });
            }
        }

        const noGenerados: string[] = [];
        for (const visitaId of idsNbs) {
            try {
                const archivo = await modeloNbsReportes.obtenerPdfVisita(visitaId);
                zip.file(archivo.nombre, archivo.blob);
            } catch (error) {
                noGenerados.push(`Visita ${visitaId}: ${mensajeDeError(error, 'No se pudo generar el reporte NBS.')}`);
            }
        }

        if (noGenerados.length === idsNbs.length && idsOtros.length === 0) {
            throw new ErrorReporteNbs(noGenerados.length === 1 ? noGenerados[0] : 'No se pudo generar ninguno de los reportes NBS seleccionados.');
        }
        if (noGenerados.length > 0) {
            zip.file('_no_generados.txt', noGenerados.join('\r\n'));
        }

        saveAs(await zip.generateAsync({ type: 'blob' }), `Reportes_${dayjs().format('YYYYMMDDHHmmss')}.zip`);
        return noGenerados.length;
    };

    // --- FUNCIONES DE DESCARGA OPTIMIZADAS ---
    const descargarArchivoUnico = (item: IVisitaCliente) => {
        try {
            const base64Data = item.ArchivoPdfBase64;
            if (base64Data === undefined || base64Data.trim() === "") {
                mostrarNotificacion(EnumTipoNotificacion.Error, 'Archivo no disponible', 'El archivo PDF para esta visita no está disponible.', 5000);
                return;
            }
            // 1. Limpiar posibles espacios o caracteres inválidos
            const cleanBase64 = base64Data.replace(/\s/g, '');

            // 2. Decodificar Base64 a binario
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            // 3. Crear el Blob con el MIME type correcto
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            // 4. Usar file-saver para descargar
            saveAs(blob, item.NombreArchivo || `Reporte_${item.IdVisita}.pdf`);

        } catch (error) {
            console.error("Error al decodificar el PDF:", error);
            // Aquí podrías disparar una notificación de error al usuario
        }
    };

    const descargarArchivosSeleccionados = async () => {
        const ids = Object.entries(idsSeleccionados)
            .filter(([, seleccionado]) => seleccionado)
            .map(([id]) => Number(id));

        if (ids.length === 0) {
            mostrarNotificacion(
                EnumTipoNotificacion.Information,
                'Atención',
                'Debe seleccionar al menos un archivo.',
                4000
            );
            return;
        }

        let idNoti: string | null = null;

        try {
            manejarCargando(true);

            // 🔹 VISITAS NBS: su archivo es el formato NBS, no el reporte de visita
            const seleccionadas = listaVisitas.filter(v => ids.includes(v.IdVisita));
            const formatos = await Promise.all(seleccionadas.map(obtenerFormatoNbs));
            const idsNbs = seleccionadas.filter((_, i) => formatos[i]).map(v => v.IdVisita);

            if (idsNbs.length > 0) {
                const idsOtros = ids.filter(id => !idsNbs.includes(id));

                if (ids.length === 1) {
                    idNoti = mostrarNotificacion(EnumTipoNotificacion.Information, 'Generando archivo', 'Estamos preparando tu archivo.', 5000, true);
                    const archivo = await modeloNbsReportes.obtenerPdfVisita(idsNbs[0]);
                    saveAs(archivo.blob, archivo.nombre);
                    removerNotificacion(idNoti);
                    mostrarNotificacion(EnumTipoNotificacion.Success, 'Descarga completada', 'El archivo se generó correctamente.', 5000);
                } else {
                    idNoti = mostrarNotificacion(
                        EnumTipoNotificacion.Information,
                        'Generando paquete ZIP',
                        'Estamos preparando tu descarga. Los reportes NBS pueden tardar unos segundos cada uno.',
                        5000,
                        true
                    );
                    const omitidos = await descargarZipConNbs(idsNbs, idsOtros);
                    removerNotificacion(idNoti);
                    if (omitidos > 0) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'Paquete listo con omisiones',
                            `${omitidos} reporte(s) NBS no se pudieron generar; el detalle está en _no_generados.txt dentro del ZIP.`,
                            7000
                        );
                    } else {
                        mostrarNotificacion(EnumTipoNotificacion.Success, 'Paquete listo', `Se han preparado ${ids.length} archivos para descarga.`, 5000);
                    }
                }

                setIdsSeleccionados({});
                return;
            }

            // 🔹 DESCARGA MASIVA
            if (ids.length > 1) {

                idNoti = mostrarNotificacion(
                    EnumTipoNotificacion.Information,
                    'Generando paquete ZIP',
                    'Estamos preparando tu descarga. Esto puede tardar unos momentos.',
                    5000,
                    true // 👈 persistente
                );

                await modeloGestionArchivos.descargarZipVisitas(ids);

                removerNotificacion(idNoti);

                mostrarNotificacion(
                    EnumTipoNotificacion.Success,
                    'Paquete listo',
                    `Se han preparado ${ids.length} archivos para descarga.`,
                    5000
                );

                setIdsSeleccionados({});
            }

            // 🔹 DESCARGA INDIVIDUAL
            else if (ids.length === 1) {

                idNoti = mostrarNotificacion(
                    EnumTipoNotificacion.Information,
                    'Generando archivo',
                    'Estamos preparando tu archivo.',
                    5000,
                    true
                );

                const respuesta = await modeloGestionArchivos.descargarArchivoVisita(ids[0]);

                if (respuesta && respuesta.Data) {
                    descargarArchivoUnico(respuesta.Data);
                    setIdsSeleccionados({});
                }

                removerNotificacion(idNoti);

                mostrarNotificacion(
                    EnumTipoNotificacion.Success,
                    'Descarga completada',
                    'El archivo se generó correctamente.',
                    5000
                );
            }

        } catch (error) {

            if (idNoti) {
                removerNotificacion(idNoti);
            }

            if (ids.length > 1) {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    'Error en descarga masiva',
                    mensajeDeError(error, 'Hubo un fallo al generar el archivo comprimido.'),
                    6000
                );
            } else {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    'Error en descarga',
                    mensajeDeError(error, 'Hubo un fallo al generar el archivo.'),
                    6000
                );
            }

            console.error(error);

        } finally {
            manejarCargando(false);
        }
    };

    const consultarDescargarBase64Visita = async (visita: IVisitaCliente) => {
        try {
            manejarCargando(true);
            if (await obtenerFormatoNbs(visita)) {
                // El indicador es un contador: se cierra el actual antes de abrir el de NBS para que el finally lo deje en cero.
                manejarCargando(false);
                manejarCargando(true, "Generando reporte NBS...");
                const archivo = await modeloNbsReportes.obtenerPdfVisita(visita.IdVisita);
                saveAs(archivo.blob, archivo.nombre);
                return;
            }

            const respuesta = await modeloGestionArchivos.descargarArchivoVisita(visita.IdVisita);
            if (respuesta && respuesta.Data) {
                descargarArchivoUnico(respuesta.Data);
            }

        } catch (error) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Ha ocurrido un error', mensajeDeError(error, 'No se ha podido descargar el archivo PDF  de la visita'), 5000)
        }
        finally {
            manejarCargando(false)
        }
    };

    const previsualizarPDF = async (visita: IVisitaCliente) => {
        try {
            manejarCargando(true, "Cargando vista previa...");
            if (await obtenerFormatoNbs(visita)) {
                // El indicador es un contador: se cierra el actual antes de abrir el de NBS para que el finally lo deje en cero.
                manejarCargando(false);
                manejarCargando(true, "Generando reporte NBS...");
                const archivo = await modeloNbsReportes.obtenerPdfVisita(visita.IdVisita);
                setUrlPDFActual(URL.createObjectURL(new File([archivo.blob], archivo.nombre, { type: 'application/pdf' })));
                setNombreArchivoActual(archivo.nombre);
                setArchivoNbsActual(archivo);
                setVisitaActual(null);
                setModalPDFAbierto(true);
                return;
            }

            const respuesta = await modeloGestionArchivos.descargarArchivoVisita(visita.IdVisita);

            if (respuesta && respuesta.Data && respuesta.Data.ArchivoPdfBase64) {
                const base64Data = respuesta.Data.ArchivoPdfBase64.replace(/\s/g, '');
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);

                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);

                // Crear blob con nombre de archivo usando File API
                const nombreArchivo = respuesta.Data.NombreArchivo || `Reporte_${visita.IdVisita}.pdf`;
                const file = new File([byteArray], nombreArchivo, { type: 'application/pdf' });
                const url = URL.createObjectURL(file);

                setUrlPDFActual(url);
                setNombreArchivoActual(nombreArchivo);
                setVisitaActual(respuesta.Data);
                setModalPDFAbierto(true);
            } else {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    'Archivo no disponible',
                    'El archivo PDF para esta visita no está disponible.',
                    5000
                );
            }
        } catch (error) {
            console.error("Error al previsualizar el PDF:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al previsualizar',
                mensajeDeError(error, 'No se pudo cargar la vista previa del archivo PDF.'),
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };

    const cerrarModalPDF = () => {
        if (urlPDFActual) {
            URL.revokeObjectURL(urlPDFActual);
        }
        setModalPDFAbierto(false);
        setUrlPDFActual("");
        setNombreArchivoActual("");
        setVisitaActual(null);
        setArchivoNbsActual(null);
    };

    const descargarDesdePrevisualizacion = () => {
        if (archivoNbsActual) {
            saveAs(archivoNbsActual.blob, archivoNbsActual.nombre);
        } else if (visitaActual) {
            descargarArchivoUnico(visitaActual);
        }
    };

    // --- MANEJO DE TABLA ---
    const columnas = useMemo(() => obtenerColumnas({
        selectedIds: idsSeleccionados,
        setSelectedIds: setIdsSeleccionados,
        consultarDescargarBase64Visita: consultarDescargarBase64Visita,
        previsualizarPDF: previsualizarPDF,
        formatosNbsPorVisita,
        tareasNbsValidadas,
        validarFormatosNbs: verFormatosNbs
    }),[idsSeleccionados, listaVisitas, formatosNbsPorVisita, tareasNbsValidadas]);

    const instanciaTabla = useReactTable({
        data: listaVisitas,
        columns: columnas as any,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            pagination: { pageIndex: indicePagina, pageSize: TAMANO_PAGINA },
            sorting: ordenamiento
        },
        onPaginationChange: (actualizador) => {
            if (typeof actualizador === 'function') {
                const nuevoEstado = actualizador({ pageIndex: indicePagina, pageSize: TAMANO_PAGINA });
                setIndicePagina(nuevoEstado.pageIndex);
            } else {
                setIndicePagina(actualizador.pageIndex ?? 0);
            }
        },
        pageCount: Math.ceil(listaVisitas.length / TAMANO_PAGINA),
        onSortingChange: setOrdenamiento,
        getSortedRowModel: getSortedRowModel(),
    });

    // Identifica en segundo plano las visitas NBS de la página visible (nombre del formato en "Archivo");
    // si falla, se vuelve a intentar al hacer clic en Ver o Descargar.
    useEffect(() => {
        if (!verFormatosNbs) return;
        const tareas = new Set(instanciaTabla.getRowModel().rows.map(r => r.original.IdTarea));
        tareas.forEach(tareaId => consultarFormatosNbsTarea(tareaId).catch(() => undefined));
    }, [listaVisitas, indicePagina, ordenamiento]);

    const cambiarEstadoHabilitarFiltros = (estaHabilitado: boolean) => {
        setFiltrosHabilitados(estaHabilitado);
    };

    const limpiarFiltro = () => {
        let proyectoIdInicial: number | undefined = undefined;

        if (!esInterno && usuario?.Identificacion) {
            const proyectosDelCliente = proyectos.filter(
                p => p.Cliente?.Identificacion?.trim() === usuario.Identificacion?.trim()
            );
            if (proyectosDelCliente.length === 1) {
                proyectoIdInicial = proyectosDelCliente[0].Id;
            }
        }

        setFiltroVisitaCliente({
            Identificacion: esInterno ? '' : usuario?.Identificacion,
            FechaInicio: '',
            FechaFin: '',
            ProyectoId: proyectoIdInicial,
            TipoServicioId: tipoTareaFijo
        });
        setListaVisitas([]);
    };



    return (
        <>
            <TablaGestionArchivosVista
                habilitarFiltros={filtrosHabilitados}
                manejarHabilitarFiltros={cambiarEstadoHabilitarFiltros}
                consultarVisitasCliente={consultarVisitasCliente}
                seleccionadas={Object.entries(idsSeleccionados).filter(([, v]) => v).map(([k]) => k)}
                descargarSeleccionados={descargarArchivosSeleccionados}
                tabla={instanciaTabla as any}
                entidadFiltro={filtroVisitaCliente}
                limpiarFiltro={limpiarFiltro}
                clientes={[]}
                Proyectos={proyectos}
                manejarCambioValorFiltro={manejarCambioFiltroVisita}
                tiposTarea={tiposTarea}
            />
            <ModalPrevisualizarPDF
                abierto={modalPDFAbierto}
                cerrar={cerrarModalPDF}
                urlPDF={urlPDFActual}
                nombreArchivo={nombreArchivoActual}
                descargarPDF={descargarDesdePrevisualizacion}
            />
        </>
    );
};

export default React.memo(GestionArchivosCV);